import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';
import { LogOut, Users, Package, TrendingUp, DollarSign, Bell, BellOff, Plus, Eye, Trash2, Settings, Download, FileText, FileSpreadsheet, MessageCircle, Shield, Edit, Calendar, CheckCircle } from 'lucide-react';
import io from 'socket.io-client';
import api from '../utils/api';
import { formatCurrency, formatDate } from '../utils/format';
import { usePushNotifications } from '../hooks/usePushNotifications';
import { registerFCMToken } from '../utils/registerFCM';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const SOCKET_PATH = "/api/socket.io";

const AdminDashboard = () => {
  const { user, logout } = useAuth();
  const { permission, requestPermission, isSupported } = usePushNotifications();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [ventas, setVentas] = useState([]);
  const [selectedVenta, setSelectedVenta] = useState(null);
  const [vendedores, setVendedores] = useState([]);
  const [administradores, setAdministradores] = useState([]);
  const [nichos, setNichos] = useState([]);
  const [stats, setStats] = useState({ total_sales: 0, total_commission: 0, count: 0 });
  const [statsByVendedor, setStatsByVendedor] = useState([]);
  const [statsByNicho, setStatsByNicho] = useState([]);
  const [comisiones, setComisiones] = useState({});
  const [notificationsEnabled, setNotificationsEnabled] = useState(true);
  const [liquidaciones, setLiquidaciones] = useState([]);
  const [fechaInicio, setFechaInicio] = useState('');
  const [fechaFin, setFechaFin] = useState('');
  const [vistaLiquidaciones, setVistaLiquidaciones] = useState('general');
  const [liquidacionesFiltradas, setLiquidacionesFiltradas] = useState(null);
  const [editLiquidacion, setEditLiquidacion] = useState(null);
  const [editLiquidacionDialogOpen, setEditLiquidacionDialogOpen] = useState(false);
  const [reporteHistorico, setReporteHistorico] = useState({});
  const socketRef = useRef(null);

  // Vendedor form
  const [newVendedor, setNewVendedor] = useState({ username: '', password: '', name: '', email: '', whatsapp_phone: '' });
  const [vendedorDialogOpen, setVendedorDialogOpen] = useState(false);

  // Admin form
  const [newAdmin, setNewAdmin] = useState({ username: '', password: '', name: '', email: '' });
  const [adminDialogOpen, setAdminDialogOpen] = useState(false);
  const [editAdmin, setEditAdmin] = useState(null);
  const [editAdminDialogOpen, setEditAdminDialogOpen] = useState(false);

  // Vendedor edit
  const [editVendedor, setEditVendedor] = useState(null);
  const [editVendedorDialogOpen, setEditVendedorDialogOpen] = useState(false);
  const [selectedImageFile, setSelectedImageFile] = useState(null);

  // Nicho form
  const [newNicho, setNewNicho] = useState({ name: '', image_url: '' });
  const [nichoDialogOpen, setNichoDialogOpen] = useState(false);
  const [editNicho, setEditNicho] = useState(null);
  const [editNichoDialogOpen, setEditNichoDialogOpen] = useState(false);

  // Comision form
  const [selectedNicho, setSelectedNicho] = useState('');
  const [tiers, setTiers] = useState([{ min_amount: 0, percentage: 5 }]);
  const [comisionDialogOpen, setComisionDialogOpen] = useState(false);

  useEffect(() => {
    fetchData();
    fetchNotificationConfig();
    initializeWebSocket();
    
    // Try to register FCM token if permissions are granted
    const tryRegisterFCM = async () => {
      if (Notification.permission === 'granted') {
        console.log('🔔 Permisos concedidos, registrando token FCM...');
        const result = await registerFCMToken();
        if (result.success) {
          toast.success('🔔 Notificaciones activadas', {
            description: 'Recibirás alertas de nuevas ventas'
          });
        }
      }
    };
    
    // Wait a bit for service worker to be ready
    setTimeout(tryRegisterFCM, 2000);

    return () => {
      if (socketRef.current?.connected) {
        socketRef.current.disconnect();
      }
    };
  }, []);

  const initializeWebSocket = () => {
    socketRef.current = io(BACKEND_URL, { path: SOCKET_PATH });

    socketRef.current.on('connect', () => {
      console.log('WebSocket connected');
      socketRef.current.emit('join_admin_room', {});
    });

    socketRef.current.on('new_sale', (data) => {
      console.log('🔔 Nueva venta recibida:', data);
      setVentas(prev => [data, ...prev]);
      
      if (notificationsEnabled) {
        toast.success(`Nueva venta de ${data.vendedor_name}`, {
          description: `${data.product_name} - ${formatCurrency(data.amount)}`
        });
      }
      
      // Refresh stats AND comisiones para actualizar todo
      console.log('🔄 Actualizando stats...');
      fetchStats().then(() => console.log('✅ Stats actualizados'));
      console.log('🔄 Actualizando comisiones...');
      fetchComisiones().then(() => console.log('✅ Comisiones actualizadas'));
    });

    socketRef.current.on('liquidacion_global_completed', (data) => {
      console.log('🔄 Liquidación global completada:', data);
      toast.success('Liquidación completada', {
        description: `${data.vendedores_liquidados} vendedores liquidados`
      });
      // Recargar todo
      fetchData();
    });

    socketRef.current.on('disconnect', () => {
      console.log('WebSocket disconnected');
    });
  };

  const fetchData = async () => {
    await Promise.all([
      fetchVentas(),
      fetchVendedores(),
      fetchAdministradores(),
      fetchNichos(),
      fetchStats(),
      fetchComisiones(),
      fetchLiquidaciones()
    ]);
  };

  const fetchVentas = async () => {
    try {
      const { data } = await api.get('/api/ventas');
      setVentas(data);
    } catch (error) {
      console.error('Error fetching ventas:', error);
    }
  };

  const fetchVendedores = async () => {
    try {
      const { data } = await api.get('/api/vendedores');
      setVendedores(data);
    } catch (error) {
      console.error('Error fetching vendedores:', error);
    }
  };

  const fetchAdministradores = async () => {
    try {
      const { data } = await api.get('/api/administradores');
      setAdministradores(data);
    } catch (error) {
      console.error('Error fetching administradores:', error);
    }
  };

  const fetchNichos = async () => {
    try {
      const { data } = await api.get('/api/nichos');
      setNichos(data);
    } catch (error) {
      console.error('Error fetching nichos:', error);
    }
  };

  const fetchStats = async () => {
    try {
      const [generalStats, vendedorStats, nichoStats] = await Promise.all([
        api.get('/api/ventas/stats'),
        api.get('/api/ventas/stats/by-vendedor'),
        api.get('/api/ventas/stats/by-nicho')
      ]);
      setStats(generalStats.data);
      setStatsByVendedor(vendedorStats.data);
      setStatsByNicho(nichoStats.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const fetchComisiones = async () => {
    try {
      const { data } = await api.get('/api/comisiones');
      const comisionesMap = {};
      data.forEach(item => {
        comisionesMap[item.nicho_id] = item.tiers;
      });
      setComisiones(comisionesMap);
    } catch (error) {
      console.error('Error fetching comisiones:', error);
    }
  };

  const fetchNotificationConfig = async () => {
    try {
      const { data } = await api.get('/api/config/notificaciones');
      setNotificationsEnabled(data.enabled);
    } catch (error) {
      console.error('Error fetching notification config:', error);
    }
  };

  const handleCreateVendedor = async (e) => {
    e.preventDefault();
    try {
      await api.post('/api/vendedores', newVendedor);
      toast.success('Vendedor creado exitosamente');
      setNewVendedor({ username: '', password: '', name: '', email: '', whatsapp_phone: '' });
      setVendedorDialogOpen(false);
      fetchVendedores();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al crear vendedor');
    }
  };

  const handleCreateAdmin = async (e) => {
    e.preventDefault();
    try {
      await api.post('/api/administradores', newAdmin);
      toast.success('Administrador creado exitosamente');
      setNewAdmin({ username: '', password: '', name: '', email: '' });
      setAdminDialogOpen(false);
      fetchAdministradores();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al crear administrador');
    }
  };

  const handleArchiveVendedor = async (username) => {
    if (!window.confirm('¿Está seguro de archivar este vendedor? Sus datos históricos se conservarán pero dejará de aparecer en las listas activas.')) return;
    
    try {
      await api.post(`/api/vendedores/${username}/archive`);
      toast.success('Vendedor archivado correctamente');
      fetchVendedores();
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al archivar vendedor');
    }
  };

  const handleDeleteAdmin = async (username) => {
    if (!window.confirm('¿Está seguro de eliminar este administrador?')) return;
    
    try {
      await api.delete(`/api/administradores/${username}`);
      toast.success('Administrador eliminado');
      fetchAdministradores();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar administrador');
    }
  };

  const handleEditAdmin = (admin) => {
    setEditAdmin({
      username: admin.username,
      name: admin.name,
      email: admin.email,
      password: ''
    });
    setEditAdminDialogOpen(true);
  };

  const handleUpdateAdmin = async (e) => {
    e.preventDefault();
    try {
      const updateData = {
        name: editAdmin.name,
        email: editAdmin.email
      };
      if (editAdmin.password) {
        updateData.password = editAdmin.password;
      }
      
      await api.put(`/api/administradores/${editAdmin.username}`, updateData);
      toast.success('Administrador actualizado');
      setEditAdminDialogOpen(false);
      fetchAdministradores();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al actualizar administrador');
    }
  };

  const handleEditVendedor = (vendedor) => {
    setEditVendedor({
      username: vendedor.username,
      name: vendedor.name,
      email: vendedor.email,
      whatsapp_phone: vendedor.whatsapp_phone || '',
      password: ''
    });
    setEditVendedorDialogOpen(true);
  };

  const handleUpdateVendedor = async (e) => {
    e.preventDefault();
    try {
      const updateData = {
        name: editVendedor.name,
        email: editVendedor.email,
        whatsapp_phone: editVendedor.whatsapp_phone
      };
      if (editVendedor.password) {
        updateData.password = editVendedor.password;
      }
      
      await api.put(`/api/vendedores/${editVendedor.username}`, updateData);
      toast.success('Vendedor actualizado');
      setEditVendedorDialogOpen(false);
      fetchVendedores();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al actualizar vendedor');
    }
  };

  const handleCreateNicho = async (e) => {
    e.preventDefault();
    try {
      await api.post('/api/nichos', newNicho);
      toast.success('Nicho creado exitosamente');
      setNewNicho({ name: '', image_url: '' });
      setNichoDialogOpen(false);
      fetchNichos();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al crear nicho');
    }
  };

  const handleDeleteNicho = async (nichoId) => {
    if (!window.confirm('¿Está seguro de eliminar este nicho?')) return;
    
    try {
      await api.delete(`/api/nichos/${nichoId}`);
      toast.success('Nicho eliminado');
      fetchNichos();
    } catch (error) {
      toast.error('Error al eliminar nicho');
    }
  };

  const handleDeleteVenta = async (ventaId) => {
    if (!window.confirm('¿Eliminar esta venta? Se restará la comisión del vendedor.')) return;
    try {
      const response = await fetch(`${BACKEND_URL}/api/ventas/${ventaId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      if (!response.ok) throw new Error('Error al eliminar');
      const data = await response.json();
      alert(`Venta eliminada. Comisión restada: $${data.comision_restada.toLocaleString()}`);
      fetchVentas();
      fetchData();
      fetchStatsByVendedor();
    } catch (err) {
      alert('Error: ' + err.message);
    }
  };

  const handleEditNicho = (nicho) => {
    setEditNicho({
      id: nicho.id,
      name: nicho.name,
      image_url: nicho.image_url || ''
    });
    setEditNichoDialogOpen(true);
  };

  const handleUpdateNicho = async (e) => {
    e.preventDefault();
    try {
      let imageUrl = editNicho.image_url;
      
      // Si hay un archivo seleccionado, subirlo primero
      if (selectedImageFile) {
        const formData = new FormData();
        formData.append('file', selectedImageFile);
        
        const uploadRes = await api.post('/api/upload/nicho-image', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        imageUrl = uploadRes.data.image_url;
      }
      
      const params = new URLSearchParams();
      if (editNicho.name) params.append('name', editNicho.name);
      if (imageUrl) params.append('image_url', imageUrl);
      
      await api.put(`/api/nichos/${editNicho.id}?${params.toString()}`);
      toast.success('Nicho actualizado exitosamente');
      setEditNichoDialogOpen(false);
      setEditNicho(null);
      setSelectedImageFile(null);
      fetchData();
    } catch (error) {
      console.error('Error:', error);
      toast.error('Error al actualizar nicho');
    }
  };

  const handleConfigureComision = async (e) => {
    e.preventDefault();
    try {
      await api.post('/api/comisiones', {
        nicho_id: selectedNicho,
        tiers: tiers
      });
      toast.success('Comisiones configuradas');
      setComisionDialogOpen(false);
      setSelectedNicho('');
      setTiers([{ min_amount: 0, percentage: 5 }]);
      fetchComisiones();
    } catch (error) {
      toast.error('Error al configurar comisiones');
    }
  };

  const handleToggleNotifications = async (enabled) => {
    try {
      await api.post('/api/config/notificaciones', { enabled });
      setNotificationsEnabled(enabled);
      toast.success(enabled ? 'Notificaciones activadas' : 'Notificaciones desactivadas');
    } catch (error) {
      toast.error('Error al actualizar configuración');
    }
  };

  const handleExportPDF = async (vendedorId = null) => {
    try {
      const url = vendedorId 
        ? `/api/reportes/vendedor/${vendedorId}/pdf`
        : '/api/reportes/general/pdf';
      
      const response = await api.get(url, {
        responseType: 'blob'
      });
      
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = `reporte_${vendedorId || 'general'}_${new Date().toISOString().split('T')[0]}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(downloadUrl);
      
      toast.success('Reporte PDF descargado');
    } catch (error) {
      toast.error('Error al generar reporte PDF');
    }
  };

  const handleExportExcel = async (vendedorId = null) => {
    try {
      const url = vendedorId 
        ? `/api/reportes/vendedor/${vendedorId}/excel`
        : '/api/reportes/general/excel';
      
      const response = await api.get(url, {
        responseType: 'blob'
      });
      
      const blob = new Blob([response.data], { 
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
      });
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = `reporte_${vendedorId || 'general'}_${new Date().toISOString().split('T')[0]}.xlsx`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(downloadUrl);
      
      toast.success('Reporte Excel descargado');
    } catch (error) {
      toast.error('Error al generar reporte Excel');
    }
  };

  const handleSendWhatsApp = async (vendedorId) => {
    try {
      const { data } = await api.get(`/api/reportes/vendedor/${vendedorId}/whatsapp-link`);
      
      // Open WhatsApp link in new window
      window.open(data.whatsapp_link, '_blank');
      
      toast.success(`WhatsApp abierto para ${data.vendedor_name}`);
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Error al generar link de WhatsApp';
      toast.error(errorMsg);
    }
  };

  const handleLiquidarVendedor = async (vendedorId, vendedorName) => {
    if (!window.confirm(`¿Liquidar comisiones de ${vendedorName}? Esto marcará todas las ventas actuales como pagadas y reseteará el contador.`)) return;
    
    try {
      const now = new Date().toISOString();
      const inicio = new Date();
      inicio.setDate(1); // Inicio del mes
      
      const { data } = await api.post('/api/liquidaciones', {
        vendedor_id: vendedorId,
        periodo_inicio: inicio.toISOString(),
        periodo_fin: now,
        notas: `Liquidación ${new Date().toLocaleDateString('es-AR')}`
      });
      
      if (data.total_comisiones > 0) {
        toast.success(`Liquidación completada: ${formatCurrency(data.total_comisiones)}`);
      } else {
        toast.info('El vendedor ya está en $0 - No hay ventas pendientes');
      }
      fetchData();
      fetchLiquidaciones();
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Error al liquidar vendedor';
      if (errorMsg.includes('No hay ventas pendientes')) {
        toast.info('El vendedor ya está en $0 - No hay ventas pendientes de liquidación');
      } else {
        toast.error(errorMsg);
      }
    }
  };

  const handleLiquidarTodos = async () => {
    if (!window.confirm('⚠️ ¿Liquidar TODOS los vendedores?\n\nEsto reseteará todos los contadores a $0 y guardará el historial del periodo actual.\n\n¿Continuar?')) return;
    
    try {
      const { data } = await api.post('/api/liquidaciones/liquidar-todos');
      
      toast.success(
        `✅ Liquidación completada: ${data.vendedores_liquidados} vendedores liquidados\n` +
        `💰 Total: ${formatCurrency(data.total_general_comisiones)}`,
        { duration: 5000 }
      );
      
      // Refrescar datos
      fetchData();
      fetchLiquidaciones();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al liquidar todos los vendedores');
    }
  };

  const fetchLiquidaciones = async () => {
    try {
      const { data } = await api.get('/api/liquidaciones');
      setLiquidaciones(data);
    } catch (error) {
      console.error('Error fetching liquidaciones:', error);
    }
  };


  
  const getStartOfWeek = (date = new Date()) => {
    const d = new Date(date);
    const day = d.getDay();
    const diff = d.getDate() - day + (day === 0 ? -6 : 1);
    d.setDate(diff);
    d.setHours(0, 0, 0, 0);
    return d;
  };

  const getEndOfWeek = (date = new Date()) => {
    const d = getStartOfWeek(date);
    d.setDate(d.getDate() + 6);
    d.setHours(23, 59, 59, 999);
    return d;
  };

  const setFiltroRapido = (tipo) => {
    const ahora = new Date();
    if (tipo === 'hoy') {
      const inicio = new Date(ahora);
      inicio.setHours(0, 0, 0, 0);
      const fin = new Date(ahora);
      fin.setHours(23, 59, 59, 999);
      setFechaInicio(inicio.toISOString().split('T')[0]);
      setFechaFin(fin.toISOString().split('T')[0]);
    } else if (tipo === 'semana') {
      const inicio = getStartOfWeek(ahora);
      const fin = getEndOfWeek(ahora);
      setFechaInicio(inicio.toISOString().split('T')[0]);
      setFechaFin(fin.toISOString().split('T')[0]);
    } else if (tipo === 'mes') {
      const inicio = new Date(ahora.getFullYear(), ahora.getMonth(), 1);
      inicio.setHours(0, 0, 0, 0);
      const fin = new Date(ahora.getFullYear(), ahora.getMonth() + 1, 0);
      fin.setHours(23, 59, 59, 999);
      setFechaInicio(inicio.toISOString().split('T')[0]);
      setFechaFin(fin.toISOString().split('T')[0]);
    }
  };

  const filtrarLiquidaciones = async () => {
    try {
      if (!fechaInicio && !fechaFin) {
        toast.error('Selecciona al menos una fecha');
        return;
      }
      const params = new URLSearchParams();
      if (fechaInicio) params.append('fecha_inicio', fechaInicio);
      if (fechaFin) params.append('fecha_fin', fechaFin);
      params.append('vista', vistaLiquidaciones);
      const { data } = await api.get(`/api/liquidaciones/filtrar?${params.toString()}`);
      setLiquidacionesFiltradas(data);
      toast.success('Filtros aplicados');
    } catch (error) {
      console.error('Error filtrando liquidaciones:', error);
      toast.error('Error al aplicar filtros');
    }
  };


  
  const filtrarVentasPeriodo = async () => {
    try {
      if (!fechaInicio || !fechaFin) {
        toast.error('Selecciona fechas de inicio y fin');
        return;
      }
      const { data } = await api.get(`/api/reportes/ventas-periodo?fecha_inicio=${fechaInicio}&fecha_fin=${fechaFin}`);
      setLiquidacionesFiltradas(data);
      toast.success('Reporte generado');
    } catch (error) {
      console.error('Error:', error);
      toast.error('Error al generar reporte');
    }
  };

  const borrarLiquidacion = async (id) => {
    if (!window.confirm('¿Eliminar esta liquidación?')) return;
    try {
      await api.delete(`/api/liquidaciones/${id}`);
      toast.success('Liquidación eliminada');
      fetchLiquidaciones();
    } catch (error) {
      toast.error('Error al eliminar');
    }
  };

  const limpiarFiltros = () => {
    setFechaInicio('');
    setFechaFin('');
    setLiquidacionesFiltradas(null);
  };

  const handleEditarLiquidacion = async () => {
    try {
      if (!editLiquidacion) return;
      await api.put(`/api/liquidaciones/${editLiquidacion.id}`, {
        periodo_inicio: editLiquidacion.periodo_inicio,
        periodo_fin: editLiquidacion.periodo_fin,
        notas: editLiquidacion.notas
      });
      toast.success('Liquidación actualizada');
      setEditLiquidacionDialogOpen(false);
      setEditLiquidacion(null);
      fetchLiquidaciones();
      if (liquidacionesFiltradas) {
        filtrarLiquidaciones();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al actualizar liquidación');
    }
  };

  const fetchReporteHistorico = async (periodo = 'mes') => {
    try {
      const { data } = await api.get(`/api/reportes/historico?periodo=${periodo}`);
      setReporteHistorico(data);
    } catch (error) {
      console.error('Error fetching reporte histórico:', error);
    }
  };


  const handleBorrarHistorial = async () => {
    if (!window.confirm('⚠️ ¿BORRAR TODO EL HISTORIAL de liquidaciones?\n\nEsta acción NO se puede deshacer.\nSolo borra el historial, NO afecta vendedores ni ventas actuales.\n\n¿Continuar?')) {
      return;
    }
    
    try {
      const response = await api.delete('/api/liquidaciones/historial');
      toast.success(`✓ Historial eliminado: ${response.data.liquidaciones_eliminadas} liquidaciones borradas`);
      await fetchLiquidaciones(); // Recargar lista vacía
    } catch (error) {
      console.error('Error borrando historial:', error);
      toast.error('Error al borrar el historial');
    }
  };

  const handleResetearSistema = async () => {
    if (!window.confirm('🚨 ¿RESETEAR TODO EL SISTEMA?\n\nEsto eliminará:\n✗ TODAS las ventas (actuales e históricas)\n✗ TODO el historial de liquidaciones\n\n✓ Mantiene: vendedores, nichos, configuración\n\nEsta acción NO se puede deshacer.\n\n¿Estás SEGURO?')) {
      return;
    }
    
    // Doble confirmación
    if (!window.confirm('ÚLTIMA CONFIRMACIÓN:\n\n¿Realmente quieres BORRAR TODAS LAS VENTAS Y EL HISTORIAL?\n\nPresiona Aceptar para continuar o Cancelar para abortar.')) {
      return;
    }
    
    try {
      const response = await api.delete('/api/sistema/reset');
      toast.success(`✓ Sistema reseteado: ${response.data.ventas_eliminadas} ventas y ${response.data.liquidaciones_eliminadas} liquidaciones eliminadas`);
      await fetchData(); // Recargar todo
      await fetchLiquidaciones();
    } catch (error) {
      console.error('Error reseteando sistema:', error);
      toast.error('Error al resetear el sistema');
    }
  };


  const addTier = () => {
    setTiers([...tiers, { min_amount: 0, percentage: 0 }]);
  };

  const removeTier = (index) => {
    setTiers(tiers.filter((_, i) => i !== index));
  };

  const updateTier = (index, field, value) => {
    const updated = [...tiers];
    updated[index][field] = parseFloat(value);
    setTiers(updated);
  };

  return (
    <div className="min-h-screen bg-[#09090b]">
      {/* Header */}
      <header className="bg-[#18181b] border-b border-[#27272a] sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-8 py-3 sm:py-4">
          <div className="flex justify-between items-center">
            <h1 className="text-lg sm:text-2xl font-bold text-[#fafafa] tracking-tight" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
              BuenasCompras END
            </h1>
            <div className="flex items-center gap-2 sm:gap-4">
              <div className="hidden sm:flex items-center gap-2">
                {notificationsEnabled ? (
                  <Bell className="w-5 h-5 text-[#10b981]" />
                ) : (
                  <BellOff className="w-5 h-5 text-[#a1a1aa]" />
                )}
                <span className="text-[#a1a1aa] text-sm" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  {user?.name}
                </span>
              </div>
              <Button
                data-testid="logout-button"
                onClick={logout}
                variant="outline"
                size="sm"
                className="border-[#27272a] text-[#fafafa] hover:bg-[#27272a] text-xs sm:text-sm"
              >
                <LogOut className="w-3 h-3 sm:w-4 sm:h-4 sm:mr-2" />
              
              {/* Push Notifications */}
              {isSupported && permission === 'default' && (
                <Button
                  onClick={async () => {
                    await requestPermission();
                    // Try to register token after permission granted
                    setTimeout(async () => {
                      const result = await registerFCMToken();
                      if (!result.success) {
                        console.error('Error registrando token después de dar permiso:', result.error);
                      }
                    }, 1000);
                  }}
                  variant="outline"
                  size="sm"
                  className="border-[#10b981] text-[#10b981] hover:bg-[#10b981]/10"
                  title="Activar notificaciones push"
                >
                  <Bell className="w-4 h-4" />
                </Button>
              )}

                <span className="hidden sm:inline">Salir</span>
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-8 py-4 sm:py-6 lg:py-8">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="bg-[#18181b] border border-[#27272a] mb-4 sm:mb-6 w-full overflow-x-auto flex-nowrap">
            <TabsTrigger data-testid="tab-dashboard" value="dashboard" className="data-[state=active]:bg-[#2563eb] data-[state=active]:text-white text-xs sm:text-sm whitespace-nowrap">
              <TrendingUp className="w-3 h-3 sm:w-4 sm:h-4 sm:mr-2" />
              <span className="hidden sm:inline">Dashboard</span>
            </TabsTrigger>
            <TabsTrigger data-testid="tab-administradores" value="administradores" className="data-[state=active]:bg-[#2563eb] data-[state=active]:text-white text-xs sm:text-sm whitespace-nowrap">
              <Shield className="w-3 h-3 sm:w-4 sm:h-4 sm:mr-2" />
              <span className="hidden sm:inline">Admins</span>
            </TabsTrigger>
            <TabsTrigger data-testid="tab-vendedores" value="vendedores" className="data-[state=active]:bg-[#2563eb] data-[state=active]:text-white text-xs sm:text-sm whitespace-nowrap">
              <Users className="w-3 h-3 sm:w-4 sm:h-4 sm:mr-2" />
              <span className="hidden sm:inline">Vendedores</span>
            </TabsTrigger>
            <TabsTrigger data-testid="tab-nichos" value="nichos" className="data-[state=active]:bg-[#2563eb] data-[state=active]:text-white text-xs sm:text-sm whitespace-nowrap">
              <Package className="w-3 h-3 sm:w-4 sm:h-4 sm:mr-2" />
              <span className="hidden sm:inline">Nichos</span>
            </TabsTrigger>
            <TabsTrigger data-testid="tab-comisiones" value="comisiones" className="data-[state=active]:bg-[#2563eb] data-[state=active]:text-white text-xs sm:text-sm whitespace-nowrap">
              <DollarSign className="w-3 h-3 sm:w-4 sm:h-4 sm:mr-2" />
              <span className="hidden sm:inline">Comisiones</span>
            </TabsTrigger>
            <TabsTrigger data-testid="tab-config" value="config" className="data-[state=active]:bg-[#2563eb] data-[state=active]:text-white text-xs sm:text-sm whitespace-nowrap">
              <Settings className="w-3 h-3 sm:w-4 sm:h-4 sm:mr-2" />
              <span className="hidden sm:inline">Config</span>
            </TabsTrigger>
            <TabsTrigger data-testid="tab-historial" value="historial" className="data-[state=active]:bg-[#2563eb] data-[state=active]:text-white text-xs sm:text-sm whitespace-nowrap">
              <Calendar className="w-3 h-3 sm:w-4 sm:h-4 sm:mr-2" />
              <span className="hidden sm:inline">Historial</span>
            </TabsTrigger>
          </TabsList>

          {/* Dashboard Tab */}
          <TabsContent value="dashboard" className="space-y-6">
            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Card className="bg-[#18181b] border-[#27272a] p-6">
                <div className="text-[#a1a1aa] text-sm mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>Total Ventas</div>
                <div className="text-3xl font-bold text-[#fafafa] tabular-nums" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
                  {formatCurrency(stats.total_sales)}
                </div>
                <div className="text-[#a1a1aa] text-xs mt-1">{stats.count} ventas</div>
              </Card>
              <Card className="bg-[#18181b] border-[#27272a] p-6">
                <div className="text-[#a1a1aa] text-sm mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>Total Comisiones</div>
                <div className="text-3xl font-bold text-[#10b981] tabular-nums" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
                  {formatCurrency(stats.total_commission)}
                </div>
              </Card>
              <Card className="bg-[#18181b] border-[#27272a] p-6">
                <div className="text-[#a1a1aa] text-sm mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>Vendedores Activos</div>
                <div className="text-3xl font-bold text-[#fafafa] tabular-nums" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
                  {vendedores.length}
                </div>
              </Card>
            </div>

            {/* Botón Liquidar Todos */}
            <Card className="bg-[#18181b] border-[#27272a] p-4">
              <div className="flex justify-between items-center">
                <div>
                  <h3 className="text-lg font-bold text-[#fafafa]" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
                    Liquidar Todos los Vendedores
                  </h3>
                  <p className="text-[#a1a1aa] text-sm mt-1">
                    Resetea todos los contadores a $0 y guarda el historial del periodo actual
                  </p>
                </div>
                <Button
                  data-testid="liquidar-todos-button"
                  onClick={handleLiquidarTodos}
                  className="bg-[#10b981] hover:bg-[#059669] text-white"
                >
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Liquidar Todos
                </Button>
              </div>
            </Card>

            {/* Real-time Sales Feed */}
            <Card className="bg-[#18181b] border-[#27272a] p-6">
              <h3 className="text-xl font-bold text-[#fafafa] mb-4" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
                Ventas en Tiempo Real
                <span className="ml-2 inline-flex items-center gap-1 text-xs text-[#10b981] font-normal">
                  <span className="w-2 h-2 bg-[#10b981] rounded-full animate-pulse"></span>
                  En vivo
                </span>
              </h3>
              <div data-testid="sales-feed" className="space-y-2 max-h-96 overflow-y-auto">
                {ventas.length === 0 ? (
                  <div className="text-[#a1a1aa] text-center py-8">No hay ventas aún</div>
                ) : (
                  ventas.map((venta) => (
                    <div
                      key={venta.id}
                      data-testid="sale-item"
                      className="flex justify-between items-center p-4 bg-[#09090b] border border-[#27272a] rounded hover:border-[#2563eb] transition-colors"
                    >
                      <div className="flex-1">
                        <div className="text-[#fafafa] font-medium">{venta.product_name}</div>
                        <div className="text-[#a1a1aa] text-sm">
                          {venta.vendedor_name} • {venta.nicho_name} • {formatDate(venta.created_at)}
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-[#fafafa] font-bold tabular-nums">{formatCurrency(venta.amount)}</div>
                        <div className="text-[#10b981] text-sm tabular-nums">+{formatCurrency(venta.commission_earned)}</div>
                      </div>
                      <Button
                        onClick={() => setSelectedVenta(venta)}
                        size="sm"
                        variant="ghost"
                        className="text-[#3b82f6] hover:bg-[#3b82f6]/10 ml-2"
                        title="Ver productos"
                      >
                        <Eye className="w-4 h-4" />
                      </Button>
<Button
                        onClick={() => handleDeleteVenta(venta.id)}
                        size="sm"
                        variant="ghost"
                        className="text-[#ef4444] hover:bg-[#ef4444]/10 ml-2"
                        title="Eliminar venta"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  ))
                )}
              </div>
            </Card>

            {/* Stats by Vendedor */}
            <Card className="bg-[#18181b] border-[#27272a] p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-xl font-bold text-[#fafafa]" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
                  Comisiones por Vendedor
                </h3>
                <div className="flex gap-2">
                  <Button
                    data-testid="export-general-pdf-button"
                    onClick={() => handleExportPDF()}
                    size="sm"
                    variant="outline"
                    className="border-[#27272a] text-[#fafafa] hover:bg-[#27272a]"
                  >
                    <FileText className="w-4 h-4 mr-2" />
                    Exportar PDF
                  </Button>
                  <Button
                    data-testid="export-general-excel-button"
                    onClick={() => handleExportExcel()}
                    size="sm"
                    variant="outline"
                    className="border-[#27272a] text-[#fafafa] hover:bg-[#27272a]"
                  >
                    <FileSpreadsheet className="w-4 h-4 mr-2" />
                    Exportar Excel
                  </Button>
                </div>
              </div>
              <div className="space-y-3">
                {statsByVendedor.map((stat) => (
                  <div key={stat.vendedor_id} className="flex justify-between items-center p-3 bg-[#09090b] border border-[#27272a] rounded">
                    <div className="flex-1">
                      <div className="text-[#fafafa] font-medium">{stat.vendedor_name}</div>
                      <div className="text-[#a1a1aa] text-sm">{stat.count} ventas</div>
                    </div>
                    <div className="text-right mr-4">
                      <div className="text-[#fafafa] font-bold tabular-nums">{formatCurrency(stat.total_sales)}</div>
                      <div className="text-[#10b981] text-sm font-bold tabular-nums">{formatCurrency(stat.total_commission)}</div>
                    </div>
                    <div className="flex gap-1">
                      <Button
                        data-testid={`liquidar-vendedor-${stat.vendedor_id}`}
                        onClick={() => handleLiquidarVendedor(stat.vendedor_id, stat.vendedor_name)}
                        size="sm"
                        variant="ghost"
                        className="text-[#10b981] hover:bg-[#10b981]/10"
                        title="Liquidar y Resetear"
                      >
                        <CheckCircle className="w-4 h-4" />
                      </Button>
                      <Button
                        data-testid={`send-whatsapp-${stat.vendedor_id}`}
                        onClick={() => handleSendWhatsApp(stat.vendedor_id)}
                        size="sm"
                        variant="ghost"
                        className="text-[#25D366] hover:bg-[#25D366]/10"
                        title="Enviar por WhatsApp"
                      >
                        <MessageCircle className="w-4 h-4" />
                      </Button>
                      <Button
                        data-testid={`export-vendedor-pdf-${stat.vendedor_id}`}
                        onClick={() => handleExportPDF(stat.vendedor_id)}
                        size="sm"
                        variant="ghost"
                        className="text-[#fafafa] hover:bg-[#27272a]"
                        title="Exportar PDF"
                      >
                        <FileText className="w-4 h-4" />
                      </Button>
                      <Button
                        data-testid={`export-vendedor-excel-${stat.vendedor_id}`}
                        onClick={() => handleExportExcel(stat.vendedor_id)}
                        size="sm"
                        variant="ghost"
                        className="text-[#fafafa] hover:bg-[#27272a]"
                        title="Exportar Excel"
                      >
                        <FileSpreadsheet className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          </TabsContent>

          {/* Administradores Tab */}
          <TabsContent value="administradores" className="space-y-6">
            <div className="flex justify-between items-center">
              <div>
                <h2 className="text-2xl font-bold text-[#fafafa]" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>Administradores</h2>
                <p className="text-[#a1a1aa] text-sm mt-1">Los 3 socios reciben ventas en tiempo real</p>
              </div>
              <Dialog open={adminDialogOpen} onOpenChange={setAdminDialogOpen}>
                <DialogTrigger asChild>
                  <Button data-testid="create-admin-button" className="bg-[#2563eb] hover:bg-[#1d4ed8] text-white">
                    <Plus className="w-4 h-4 mr-2" />
                    Nuevo Administrador
                  </Button>
                </DialogTrigger>
                <DialogContent className="bg-[#18181b] border-[#27272a] text-[#fafafa]">
                  <DialogHeader>
                    <DialogTitle>Crear Administrador</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleCreateAdmin} className="space-y-4">
                    <div>
                      <Label htmlFor="admin-username">Usuario</Label>
                      <Input
                        id="admin-username"
                        data-testid="admin-username-input"
                        value={newAdmin.username}
                        onChange={(e) => setNewAdmin({ ...newAdmin, username: e.target.value })}
                        className="bg-[#09090b] border-[#27272a] text-[#fafafa]"
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="admin-password">Contraseña</Label>
                      <Input
                        id="admin-password"
                        data-testid="admin-password-input"
                        type="password"
                        value={newAdmin.password}
                        onChange={(e) => setNewAdmin({ ...newAdmin, password: e.target.value })}
                        className="bg-[#09090b] border-[#27272a] text-[#fafafa]"
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="admin-name">Nombre</Label>
                      <Input
                        id="admin-name"
                        data-testid="admin-name-input"
                        value={newAdmin.name}
                        onChange={(e) => setNewAdmin({ ...newAdmin, name: e.target.value })}
                        className="bg-[#09090b] border-[#27272a] text-[#fafafa]"
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="admin-email">Email</Label>
                      <Input
                        id="admin-email"
                        data-testid="admin-email-input"
                        type="email"
                        value={newAdmin.email}
                        onChange={(e) => setNewAdmin({ ...newAdmin, email: e.target.value })}
                        className="bg-[#09090b] border-[#27272a] text-[#fafafa]"
                        required
                      />
                    </div>
                    <Button data-testid="submit-admin-button" type="submit" className="w-full bg-[#2563eb] hover:bg-[#1d4ed8] text-white">
                      Crear Administrador
                    </Button>
                  </form>
                </DialogContent>
              </Dialog>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {administradores.map((admin) => (
                <Card key={admin.username} className="bg-[#18181b] border-[#27272a] p-6">
                  <div className="flex justify-between items-start mb-4">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 bg-[#2563eb] rounded-full flex items-center justify-center">
                        <Shield className="w-6 h-6 text-white" />
                      </div>
                      <div>
                        <div className="text-[#fafafa] font-bold text-lg">{admin.name}</div>
                        <div className="text-[#a1a1aa] text-sm">@{admin.username}</div>
                      </div>
                    </div>
                    <div className="flex gap-1">
                      <Button
                        data-testid={`edit-admin-${admin.username}`}
                        onClick={() => handleEditAdmin(admin)}
                        variant="ghost"
                        size="sm"
                        className="text-[#fafafa] hover:bg-[#27272a]"
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button
                        data-testid={`delete-admin-${admin.username}`}
                        onClick={() => handleDeleteAdmin(admin.username)}
                        variant="ghost"
                        size="sm"
                        className="text-[#ef4444] hover:bg-[#ef4444]/10"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                  <div className="text-[#a1a1aa] text-sm">{admin.email}</div>
                  <div className="mt-4 flex items-center gap-2 text-[#10b981] text-sm">
                    <Bell className="w-4 h-4" />
                    Recibe ventas en tiempo real
                  </div>
                </Card>
              ))}
            </div>
            
            {/* Edit Admin Dialog */}
            <Dialog open={editAdminDialogOpen} onOpenChange={setEditAdminDialogOpen}>
              <DialogContent className="bg-[#18181b] border-[#27272a] text-[#fafafa]">
                <DialogHeader>
                  <DialogTitle>Editar Administrador</DialogTitle>
                </DialogHeader>
                {editAdmin && (
                  <form onSubmit={handleUpdateAdmin} className="space-y-4">
                    <div>
                      <Label>Usuario</Label>
                      <Input
                        value={editAdmin.username}
                        disabled
                        className="bg-[#27272a] border-[#27272a] text-[#a1a1aa]"
                      />
                    </div>
                    <div>
                      <Label htmlFor="edit-admin-name">Nombre</Label>
                      <Input
                        id="edit-admin-name"
                        value={editAdmin.name}
                        onChange={(e) => setEditAdmin({ ...editAdmin, name: e.target.value })}
                        className="bg-[#09090b] border-[#27272a] text-[#fafafa]"
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="edit-admin-email">Email</Label>
                      <Input
                        id="edit-admin-email"
                        type="email"
                        value={editAdmin.email}
                        onChange={(e) => setEditAdmin({ ...editAdmin, email: e.target.value })}
                        className="bg-[#09090b] border-[#27272a] text-[#fafafa]"
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="edit-admin-password">Nueva Contraseña (dejar vacío para no cambiar)</Label>
                      <Input
                        id="edit-admin-password"
                        type="password"
                        value={editAdmin.password}
                        onChange={(e) => setEditAdmin({ ...editAdmin, password: e.target.value })}
                        className="bg-[#09090b] border-[#27272a] text-[#fafafa]"
                        placeholder="Dejar vacío si no quieres cambiarla"
                      />
                    </div>
                    <Button type="submit" className="w-full bg-[#2563eb] hover:bg-[#1d4ed8] text-white">
                      Actualizar
                    </Button>
                  </form>
                )}
              </DialogContent>
            </Dialog>
          </TabsContent>

          {/* Vendedores Tab */}
          <TabsContent value="vendedores" className="space-y-6">
            <div className="flex justify-between items-center">
              <h2 className="text-2xl font-bold text-[#fafafa]" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>Vendedores</h2>
              <Dialog open={vendedorDialogOpen} onOpenChange={setVendedorDialogOpen}>
                <DialogTrigger asChild>
                  <Button data-testid="create-vendedor-button" className="bg-[#2563eb] hover:bg-[#1d4ed8] text-white">
                    <Plus className="w-4 h-4 mr-2" />
                    Nuevo Vendedor
                  </Button>
                </DialogTrigger>
                <DialogContent className="bg-[#18181b] border-[#27272a] text-[#fafafa]">
                  <DialogHeader>
                    <DialogTitle>Crear Vendedor</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleCreateVendedor} className="space-y-4">
                    <div>
                      <Label htmlFor="vendedor-username">Usuario</Label>
                      <Input
                        id="vendedor-username"
                        data-testid="vendedor-username-input"
                        value={newVendedor.username}
                        onChange={(e) => setNewVendedor({ ...newVendedor, username: e.target.value })}
                        className="bg-[#09090b] border-[#27272a] text-[#fafafa]"
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="vendedor-password">Contraseña</Label>
                      <Input
                        id="vendedor-password"
                        data-testid="vendedor-password-input"
                        type="password"
                        value={newVendedor.password}
                        onChange={(e) => setNewVendedor({ ...newVendedor, password: e.target.value })}
                        className="bg-[#09090b] border-[#27272a] text-[#fafafa]"
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="vendedor-name">Nombre</Label>
                      <Input
                        id="vendedor-name"
                        data-testid="vendedor-name-input"
                        value={newVendedor.name}
                        onChange={(e) => setNewVendedor({ ...newVendedor, name: e.target.value })}
                        className="bg-[#09090b] border-[#27272a] text-[#fafafa]"
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="vendedor-email">Email</Label>
                      <Input
                        id="vendedor-email"
                        data-testid="vendedor-email-input"
                        type="email"
                        value={newVendedor.email}
                        onChange={(e) => setNewVendedor({ ...newVendedor, email: e.target.value })}
                        className="bg-[#09090b] border-[#27272a] text-[#fafafa]"
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="vendedor-whatsapp">WhatsApp (con código de país)</Label>
                      <Input
                        id="vendedor-whatsapp"
                        data-testid="vendedor-whatsapp-input"
                        type="tel"
                        placeholder="Ej: 5491112345678"
                        value={newVendedor.whatsapp_phone}
                        onChange={(e) => setNewVendedor({ ...newVendedor, whatsapp_phone: e.target.value })}
                        className="bg-[#09090b] border-[#27272a] text-[#fafafa]"
                      />
                      <p className="text-[#a1a1aa] text-xs mt-1">Incluir código de país sin el + (ej: 549 para Argentina)</p>
                    </div>
                    <Button data-testid="submit-vendedor-button" type="submit" className="w-full bg-[#2563eb] hover:bg-[#1d4ed8] text-white">
                      Crear Vendedor
                    </Button>
                  </form>
                </DialogContent>
              </Dialog>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {vendedores.map((vendedor) => (
                <Card key={vendedor.username} className="bg-[#18181b] border-[#27272a] p-6">
                  <div className="flex justify-between items-start mb-4">
                    <div>
                      <div className="text-[#fafafa] font-bold text-lg">{vendedor.name}</div>
                      <div className="text-[#a1a1aa] text-sm">@{vendedor.username}</div>
                    </div>
                    <div className="flex gap-1">
                      <Button
                        data-testid={`edit-vendedor-${vendedor.username}`}
                        onClick={() => handleEditVendedor(vendedor)}
                        variant="ghost"
                        size="sm"
                        className="text-[#fafafa] hover:bg-[#27272a]"
                        title="Editar"
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button
                        data-testid={`archive-vendedor-${vendedor.username}`}
                        onClick={() => handleArchiveVendedor(vendedor.username)}
                        variant="ghost"
                        size="sm"
                        className="text-[#f59e0b] hover:bg-[#f59e0b]/10"
                        title="Archivar"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                  <div className="text-[#a1a1aa] text-sm">{vendedor.email}</div>
                  {vendedor.whatsapp_phone && (
                    <div className="text-[#10b981] text-sm mt-2">📱 {vendedor.whatsapp_phone}</div>
                  )}
                  
                  {/* Botones de exportación y WhatsApp */}
                  <div className="mt-4 flex gap-2 flex-wrap">
                    <Button
                      data-testid={`vendedor-list-whatsapp-${vendedor.username}`}
                      onClick={() => handleSendWhatsApp(vendedor.id)}
                      size="sm"
                      variant="outline"
                      className="border-[#25D366] text-[#25D366] hover:bg-[#25D366]/10 flex-1 min-w-[120px]"
                    >
                      <MessageCircle className="w-4 h-4 mr-2" />
                      WhatsApp
                    </Button>
                    <Button
                      data-testid={`vendedor-list-pdf-${vendedor.username}`}
                      onClick={() => handleExportPDF(vendedor.id)}
                      size="sm"
                      variant="outline"
                      className="border-[#27272a] text-[#fafafa] hover:bg-[#27272a]"
                      title="Exportar PDF"
                    >
                      <FileText className="w-4 h-4" />
                    </Button>
                    <Button
                      data-testid={`vendedor-list-excel-${vendedor.username}`}
                      onClick={() => handleExportExcel(vendedor.id)}
                      size="sm"
                      variant="outline"
                      className="border-[#27272a] text-[#fafafa] hover:bg-[#27272a]"
                      title="Exportar Excel"
                    >
                      <FileSpreadsheet className="w-4 h-4" />
                    </Button>
                  </div>
                </Card>
              ))}
            </div>
            
            {/* Edit Vendedor Dialog */}
            <Dialog open={editVendedorDialogOpen} onOpenChange={setEditVendedorDialogOpen}>
              <DialogContent className="bg-[#18181b] border-[#27272a] text-[#fafafa]">
                <DialogHeader>
                  <DialogTitle>Editar Vendedor</DialogTitle>
                </DialogHeader>
                {editVendedor && (
                  <form onSubmit={handleUpdateVendedor} className="space-y-4">
                    <div>
                      <Label>Usuario</Label>
                      <Input
                        value={editVendedor.username}
                        disabled
                        className="bg-[#27272a] border-[#27272a] text-[#a1a1aa]"
                      />
                    </div>
                    <div>
                      <Label htmlFor="edit-vendedor-name">Nombre</Label>
                      <Input
                        id="edit-vendedor-name"
                        value={editVendedor.name}
                        onChange={(e) => setEditVendedor({ ...editVendedor, name: e.target.value })}
                        className="bg-[#09090b] border-[#27272a] text-[#fafafa]"
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="edit-vendedor-email">Email</Label>
                      <Input
                        id="edit-vendedor-email"
                        type="email"
                        value={editVendedor.email}
                        onChange={(e) => setEditVendedor({ ...editVendedor, email: e.target.value })}
                        className="bg-[#09090b] border-[#27272a] text-[#fafafa]"
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="edit-vendedor-whatsapp">WhatsApp</Label>
                      <Input
                        id="edit-vendedor-whatsapp"
                        value={editVendedor.whatsapp_phone}
                        onChange={(e) => setEditVendedor({ ...editVendedor, whatsapp_phone: e.target.value })}
                        className="bg-[#09090b] border-[#27272a] text-[#fafafa]"
                        placeholder="5491112345678"
                      />
                    </div>
                    <div>
                      <Label htmlFor="edit-vendedor-password">Nueva Contraseña (dejar vacío para no cambiar)</Label>
                      <Input
                        id="edit-vendedor-password"
                        type="password"
                        value={editVendedor.password}
                        onChange={(e) => setEditVendedor({ ...editVendedor, password: e.target.value })}
                        className="bg-[#09090b] border-[#27272a] text-[#fafafa]"
                        placeholder="Dejar vacío si no quieres cambiarla"
                      />
                    </div>
                    <Button type="submit" className="w-full bg-[#2563eb] hover:bg-[#1d4ed8] text-white">
                      Actualizar
                    </Button>
                  </form>
                )}
              </DialogContent>
            </Dialog>
          </TabsContent>

          {/* Nichos Tab */}
          <TabsContent value="nichos" className="space-y-6">
            <div className="flex justify-between items-center">
              <h2 className="text-2xl font-bold text-[#fafafa]" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>Nichos</h2>
              <Dialog open={nichoDialogOpen} onOpenChange={setNichoDialogOpen}>
                <DialogTrigger asChild>
                  <Button data-testid="create-nicho-button" className="bg-[#2563eb] hover:bg-[#1d4ed8] text-white">
                    <Plus className="w-4 h-4 mr-2" />
                    Nuevo Nicho
                  </Button>
                </DialogTrigger>
                <DialogContent className="bg-[#18181b] border-[#27272a] text-[#fafafa]">
                  <DialogHeader>
                    <DialogTitle>Crear Nicho</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleCreateNicho} className="space-y-4">
                    <div>
                      <Label htmlFor="nicho-name">Nombre</Label>
                      <Input
                        id="nicho-name"
                        data-testid="nicho-name-input"
                        value={newNicho.name}
                        onChange={(e) => setNewNicho({ ...newNicho, name: e.target.value })}
                        className="bg-[#09090b] border-[#27272a] text-[#fafafa]"
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="nicho-image">URL de Imagen (opcional)</Label>
                      <Input
                        id="nicho-image"
                        data-testid="nicho-image-input"
                        value={newNicho.image_url}
                        onChange={(e) => setNewNicho({ ...newNicho, image_url: e.target.value })}
                        className="bg-[#09090b] border-[#27272a] text-[#fafafa]"
                      />
                    </div>
                    <Button data-testid="submit-nicho-button" type="submit" className="w-full bg-[#2563eb] hover:bg-[#1d4ed8] text-white">
                      Crear Nicho
                    </Button>
                  </form>
                </DialogContent>
              </Dialog>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {nichos.map((nicho) => (
                <Card key={nicho.id} className="bg-[#18181b] border-[#27272a] overflow-hidden">
                  {nicho.image_url && (
                    <div className="h-40 bg-[#09090b]">
                      <img src={nicho.image_url} alt={nicho.name} className="w-full h-full object-cover" />
                    </div>
                  )}
                  <div className="p-6">
                    <div className="flex justify-between items-center mb-2">
                      <div className="text-[#fafafa] font-bold text-lg">{nicho.name}</div>
                      <div className="flex gap-2">
                        <Button
                          onClick={() => handleEditNicho(nicho)}
                          variant="ghost"
                          size="sm"
                          className="text-[#2563eb] hover:bg-[#2563eb]/10"
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button
                          data-testid={`delete-nicho-${nicho.id}`}
                          onClick={() => handleDeleteNicho(nicho.id)}
                          variant="ghost"
                          size="sm"
                          className="text-[#ef4444] hover:bg-[#ef4444]/10"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                </Card>
              ))}
            </div>

            {/* Dialog para editar nicho */}
            <Dialog open={editNichoDialogOpen} onOpenChange={setEditNichoDialogOpen}>
              <DialogContent className="bg-[#18181b] border-[#27272a] text-[#fafafa]">
                <DialogHeader>
                  <DialogTitle>Editar Nicho</DialogTitle>
                </DialogHeader>
                {editNicho && (
                  <form onSubmit={handleUpdateNicho} className="space-y-4">
                    <div>
                      <Label htmlFor="edit-nicho-name">Nombre</Label>
                      <Input
                        id="edit-nicho-name"
                        value={editNicho.name}
                        onChange={(e) => setEditNicho({ ...editNicho, name: e.target.value })}
                        className="bg-[#09090b] border-[#27272a] text-[#fafafa]"
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="edit-nicho-image">Imagen del Nicho</Label>
                      <div className="space-y-2">
                        <Input
                          id="edit-nicho-image-file"
                          type="file"
                          accept="image/*"
                          onChange={(e) => {
                            const file = e.target.files[0];
                            if (file) {
                              setSelectedImageFile(file);
                              // Mostrar vista previa del archivo local
                              const reader = new FileReader();
                              reader.onload = (e) => {
                                setEditNicho({ ...editNicho, image_url: e.target.result });
                              };
                              reader.readAsDataURL(file);
                            }
                          }}
                          className="bg-[#09090b] border-[#27272a] text-[#fafafa]"
                        />
                        <p className="text-xs text-[#71717a]">O ingresa una URL de imagen:</p>
                        <Input
                          id="edit-nicho-image-url"
                          value={selectedImageFile ? '' : (editNicho.image_url || '')}
                          onChange={(e) => {
                            setEditNicho({ ...editNicho, image_url: e.target.value });
                            setSelectedImageFile(null);
                          }}
                          disabled={!!selectedImageFile}
                          className="bg-[#09090b] border-[#27272a] text-[#fafafa]"
                          placeholder="https://ejemplo.com/imagen.jpg"
                        />
                      </div>
                    </div>
                    {editNicho.image_url && (
                      <div className="mt-2">
                        <Label>Vista previa:</Label>
                        <div className="mt-2 border border-[#27272a] rounded-lg overflow-hidden">
                          <img
                            src={editNicho.image_url}
                            alt="Preview"
                            className="w-full h-40 object-cover"
                            onError={(e) => {
                              e.target.style.display = 'none';
                            }}
                          />
                        </div>
                      </div>
                    )}
                    <Button type="submit" className="w-full bg-[#2563eb] hover:bg-[#1d4ed8] text-white">
                      Actualizar Nicho
                    </Button>
                  </form>
                )}
              </DialogContent>
            </Dialog>
          </TabsContent>

          {/* Comisiones Tab */}
          <TabsContent value="comisiones" className="space-y-6">
            <div className="flex justify-between items-center">
              <h2 className="text-2xl font-bold text-[#fafafa]" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>Configuración de Comisiones</h2>
              <Dialog open={comisionDialogOpen} onOpenChange={setComisionDialogOpen}>
                <DialogTrigger asChild>
                  <Button data-testid="configure-comision-button" className="bg-[#2563eb] hover:bg-[#1d4ed8] text-white">
                    <Settings className="w-4 h-4 mr-2" />
                    Configurar Comisiones
                  </Button>
                </DialogTrigger>
                <DialogContent className="bg-[#18181b] border-[#27272a] text-[#fafafa] max-w-2xl">
                  <DialogHeader>
                    <DialogTitle>Configurar Comisiones por Nicho</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleConfigureComision} className="space-y-4">
                    <div>
                      <Label htmlFor="comision-nicho">Nicho</Label>
                      <select
                        id="comision-nicho"
                        data-testid="comision-nicho-select"
                        value={selectedNicho}
                        onChange={(e) => setSelectedNicho(e.target.value)}
                        className="w-full p-2 bg-[#09090b] border border-[#27272a] text-[#fafafa] rounded"
                        required
                      >
                        <option value="">Seleccionar nicho</option>
                        {nichos.map((nicho) => (
                          <option key={nicho.id} value={nicho.id}>{nicho.name}</option>
                        ))}
                      </select>
                    </div>

                    <div className="space-y-3">
                      <Label>Escalas de Comisión</Label>
                      {tiers.map((tier, index) => (
                        <div key={index} className="flex gap-2 items-center">
                          <div className="flex-1">
                            <Input
                              data-testid={`tier-min-${index}`}
                              type="number"
                              placeholder="Monto mínimo"
                              value={tier.min_amount}
                              onChange={(e) => updateTier(index, 'min_amount', e.target.value)}
                              className="bg-[#09090b] border-[#27272a] text-[#fafafa]"
                              required
                            />
                          </div>
                          <div className="flex-1">
                            <Input
                              data-testid={`tier-percentage-${index}`}
                              type="number"
                              step="0.01"
                              placeholder="Porcentaje"
                              value={tier.percentage}
                              onChange={(e) => updateTier(index, 'percentage', e.target.value)}
                              className="bg-[#09090b] border-[#27272a] text-[#fafafa]"
                              required
                            />
                          </div>
                          {index > 0 && (
                            <Button
                              type="button"
                              onClick={() => removeTier(index)}
                              variant="ghost"
                              size="sm"
                              className="text-[#ef4444]"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      ))}
                      <Button
                        data-testid="add-tier-button"
                        type="button"
                        onClick={addTier}
                        variant="outline"
                        className="border-[#27272a] text-[#fafafa]"
                      >
                        <Plus className="w-4 h-4 mr-2" />
                        Agregar Escala
                      </Button>
                    </div>

                    <Button data-testid="submit-comision-button" type="submit" className="w-full bg-[#2563eb] hover:bg-[#1d4ed8] text-white">
                      Guardar Configuración
                    </Button>
                  </form>
                </DialogContent>
              </Dialog>
            </div>

            <div className="space-y-4">
              {nichos.map((nicho) => (
                <Card key={nicho.id} className="bg-[#18181b] border-[#27272a] p-6">
                  <h3 className="text-lg font-bold text-[#fafafa] mb-4">{nicho.name}</h3>
                  {comisiones[nicho.id] && comisiones[nicho.id].length > 0 ? (
                    <div className="space-y-2">
                      {comisiones[nicho.id].map((tier, index) => (
                        <div key={index} className="flex justify-between p-3 bg-[#09090b] border border-[#27272a] rounded">
                          <span className="text-[#fafafa]">Desde {formatCurrency(tier.min_amount)}</span>
                          <span className="text-[#10b981] font-bold">{tier.percentage}%</span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-[#a1a1aa] text-sm">Sin configuración de comisiones</div>
                  )}
                </Card>
              ))}
            </div>
          </TabsContent>

          {/* Config Tab */}
          <TabsContent value="config" className="space-y-6">
            <Card className="bg-[#18181b] border-[#27272a] p-6">
              <h3 className="text-xl font-bold text-[#fafafa] mb-6" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
                Configuración de Notificaciones
              </h3>
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-[#fafafa] font-medium">Notificaciones de Ventas</div>
                  <div className="text-[#a1a1aa] text-sm">Recibir notificaciones cuando se registre una nueva venta</div>
                </div>
                <Switch
                  data-testid="notifications-toggle"
                  checked={notificationsEnabled}
                  onCheckedChange={handleToggleNotifications}
                  className="data-[state=checked]:bg-[#10b981]"
                />
              </div>
            </Card>
          </TabsContent>

          {/* Historial Tab */}
          <TabsContent value="historial" className="space-y-6">
            <Card className="bg-[#18181b] border-[#27272a] p-6">
              
              {/* Header */}
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-xl font-bold text-[#fafafa]">Historial de Liquidaciones</h3>
                <div className="flex gap-2">
                  <Button onClick={handleBorrarHistorial} variant="outline" size="sm" className="border-orange-500 text-orange-500">
                    🗑️ Borrar Historial
                  </Button>
                </div>
              </div>

              {/* Filtros */}
              <Card className="bg-[#09090b] border-[#27272a] p-4 mb-6">
                <h4 className="text-md font-semibold text-[#fafafa] mb-4">🔍 Filtrar Liquidaciones</h4>
                
                {/* Botones rápidos */}
                <div className="flex gap-2 mb-4 flex-wrap">
                  <Button onClick={() => setFiltroRapido('hoy')} variant="outline" size="sm" className="border-[#27272a] text-[#fafafa] hover:bg-[#27272a]">
                    📅 Hoy
                  </Button>
                  <Button onClick={() => setFiltroRapido('semana')} variant="outline" size="sm" className="border-[#27272a] text-[#fafafa] hover:bg-[#27272a]">
                    📆 Esta Semana
                  </Button>
                  <Button onClick={() => setFiltroRapido('mes')} variant="outline" size="sm" className="border-[#27272a] text-[#fafafa] hover:bg-[#27272a]">
                    🗓️ Este Mes
                  </Button>
                </div>

                {/* Selectores de fecha */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <Label className="text-[#a1a1aa] mb-2">Fecha Inicio</Label>
                    <Input type="date" value={fechaInicio} onChange={(e) => setFechaInicio(e.target.value)} className="bg-[#18181b] border-[#27272a] text-[#fafafa]" />
                  </div>
                  <div>
                    <Label className="text-[#a1a1aa] mb-2">Fecha Fin</Label>
                    <Input type="date" value={fechaFin} onChange={(e) => setFechaFin(e.target.value)} className="bg-[#18181b] border-[#27272a] text-[#fafafa]" />
                  </div>
                </div>

                {/* Selector de vista */}
                <div className="mb-4">
                  <Label className="text-[#a1a1aa] mb-2">Vista</Label>
                  <div className="flex gap-2">
                    <Button onClick={() => setVistaLiquidaciones('general')} variant={vistaLiquidaciones === 'general' ? 'default' : 'outline'} size="sm" className={vistaLiquidaciones === 'general' ? 'bg-[#10b981]' : 'border-[#27272a] text-[#fafafa]'}>
                      📋 General
                    </Button>
                    <Button onClick={() => setVistaLiquidaciones('por_dia')} variant={vistaLiquidaciones === 'por_dia' ? 'default' : 'outline'} size="sm" className={vistaLiquidaciones === 'por_dia' ? 'bg-[#10b981]' : 'border-[#27272a] text-[#fafafa]'}>
                      📊 Por Día
                    </Button>
                    <Button onClick={() => setVistaLiquidaciones('por_vendedor')} variant={vistaLiquidaciones === 'por_vendedor' ? 'default' : 'outline'} size="sm" className={vistaLiquidaciones === 'por_vendedor' ? 'bg-[#10b981]' : 'border-[#27272a] text-[#fafafa]'}>
                      👥 Por Vendedor
                    </Button>
                  </div>
                </div>

                {/* Botones de acción */}
                <div className="flex gap-2 flex-wrap">
                  <Button onClick={filtrarVentasPeriodo} className="bg-[#10b981] hover:bg-[#059669] flex-1">
                    ✓ Aplicar Filtros
                  </Button>
                  {liquidacionesFiltradas && liquidacionesFiltradas.total_ventas !== undefined && (
                    <>
                      <Button 
                        onClick={() => window.open(`${process.env.REACT_APP_BACKEND_URL}/api/reportes/ventas-periodo/pdf?fecha_inicio=${fechaInicio}&fecha_fin=${fechaFin}`, '_blank')}
                        variant="outline" 
                        className="border-red-500 text-red-500 hover:bg-red-500/10"
                      >
                        📄 PDF
                      </Button>
                      <Button 
                        onClick={() => window.open(`${process.env.REACT_APP_BACKEND_URL}/api/reportes/ventas-periodo/excel?fecha_inicio=${fechaInicio}&fecha_fin=${fechaFin}`, '_blank')}
                        variant="outline" 
                        className="border-green-500 text-green-500 hover:bg-green-500/10"
                      >
                        📊 Excel
                      </Button>
                      <Button onClick={limpiarFiltros} variant="outline" className="border-[#27272a] text-[#fafafa]">
                        ✕ Limpiar
                      </Button>
                    </>
                  )}
                </div>
              </Card>

              {/* Resultados */}
              <div className="space-y-4">
                
                {/* Mostrar totales si es reporte de ventas */}
                {liquidacionesFiltradas && liquidacionesFiltradas.total_ventas !== undefined && (
                  <>
                    <Card className="bg-[#10b981]/10 border-[#10b981] p-6 mb-6">
                      <h4 className="text-xl font-bold text-[#fafafa] mb-4">📊 Resumen del Período</h4>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div className="bg-[#09090b] p-4 rounded">
                          <div className="text-[#a1a1aa] text-sm">Total Ventas</div>
                          <div className="text-2xl font-bold text-[#fafafa] mt-1">{formatCurrency(liquidacionesFiltradas.total_ventas)}</div>
                        </div>
                        <div className="bg-[#09090b] p-4 rounded">
                          <div className="text-[#a1a1aa] text-sm">Total Comisiones</div>
                          <div className="text-2xl font-bold text-[#10b981] mt-1">{formatCurrency(liquidacionesFiltradas.total_comisiones)}</div>
                        </div>
                        <div className="bg-[#09090b] p-4 rounded">
                          <div className="text-[#a1a1aa] text-sm">Cantidad Ventas</div>
                          <div className="text-2xl font-bold text-[#fafafa] mt-1">{liquidacionesFiltradas.cantidad_ventas}</div>
                        </div>
                      </div>
                    </Card>

                    {/* Desglose por vendedor */}
                    {vistaLiquidaciones === 'por_vendedor' && liquidacionesFiltradas.por_vendedor && liquidacionesFiltradas.por_vendedor.length > 0 && (
                      <div className="space-y-3 mb-6">
                        <h4 className="text-lg font-bold text-[#fafafa]">👥 Desglose por Vendedor</h4>
                        {liquidacionesFiltradas.por_vendedor.map((vendedor, idx) => (
                          <Card key={idx} className="bg-[#09090b] border-[#27272a] p-4">
                            <div className="flex justify-between items-center">
                              <div>
                                <div className="text-[#fafafa] font-bold">{vendedor.vendedor_name}</div>
                                <div className="text-[#a1a1aa] text-sm">{vendedor.cantidad} ventas</div>
                              </div>
                              <div className="flex gap-6 text-right">
                                <div>
                                  <div className="text-[#a1a1aa] text-xs">Ventas</div>
                                  <div className="text-[#fafafa] font-bold">{formatCurrency(vendedor.total_ventas)}</div>
                                </div>
                                <div>
                                  <div className="text-[#a1a1aa] text-xs">Comisiones</div>
                                  <div className="text-[#10b981] font-bold">{formatCurrency(vendedor.total_comisiones)}</div>
                                </div>
                              </div>
                            </div>
                          </Card>
                        ))}
                      </div>
                    )}
                  </>
                )}

                <h4 className="text-lg font-bold text-[#fafafa]">
                  {liquidacionesFiltradas ? 'Resultados Filtrados' : 'Liquidaciones Completadas'}
                </h4>
                
                {/* Vista General */}
                {(!liquidacionesFiltradas || liquidacionesFiltradas.vista === 'general') && (
                  <>
                    {(liquidacionesFiltradas?.liquidaciones || liquidaciones).length > 0 ? (
                      <div className="space-y-3">
                        {(liquidacionesFiltradas?.liquidaciones || liquidaciones).map((liq) => (
                          <Card key={liq.id} className="bg-[#09090b] border-[#27272a] p-4">
                            <div className="flex justify-between items-center">
                              <div>
                                <div className="text-[#fafafa] font-medium">{liq.vendedor_name}</div>
                                <div className="text-[#a1a1aa] text-sm">
                                  {formatDate(liq.periodo_inicio)} - {formatDate(liq.periodo_fin)}
                                </div>
                              </div>
                              <div className="flex gap-4 items-center">
                                <div className="text-right">
                                  <div className="text-[#a1a1aa] text-xs">Comisiones</div>
                                  <div className="text-[#10b981] font-bold">{formatCurrency(liq.total_comisiones)}</div>
                                </div>
                                <Button onClick={() => borrarLiquidacion(liq.id)} variant="outline" size="sm" className="border-red-500 text-red-500 hover:bg-red-500/10">
                                  🗑️
                                </Button>
                              </div>
                            </div>
                          </Card>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-12 text-[#a1a1aa]">No hay liquidaciones</div>
                    )}
                  </>
                )}

                {/* Vista Por Día */}
                {liquidacionesFiltradas && liquidacionesFiltradas.vista === 'por_dia' && liquidacionesFiltradas.datos && (
                  <div className="space-y-4">
                    {liquidacionesFiltradas.datos.map((dia) => (
                      <Card key={dia.fecha} className="bg-[#09090b] border-[#27272a] p-4">
                        <div className="text-[#fafafa] font-bold mb-2">📅 {dia.fecha}</div>
                        <div className="text-[#10b981]">Total: {formatCurrency(dia.total_comisiones)}</div>
                      </Card>
                    ))}
                  </div>
                )}

                {/* Vista Por Vendedor */}
                {liquidacionesFiltradas && liquidacionesFiltradas.vista === 'por_vendedor' && liquidacionesFiltradas.datos && (
                  <div className="space-y-4">
                    {liquidacionesFiltradas.datos.map((vendedor) => (
                      <Card key={vendedor.vendedor_id} className="bg-[#09090b] border-[#27272a] p-4">
                        <div className="text-[#fafafa] font-bold mb-2">👤 {vendedor.vendedor_name}</div>
                        <div className="text-[#10b981]">Total: {formatCurrency(vendedor.total_comisiones)}</div>
                      </Card>
                    ))}
                  </div>
                )}
              </div>
            </Card>
          </TabsContent>
        </Tabs>
      </main>

      {/* Modal Ver Productos */}
      {selectedVenta && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setSelectedVenta(null)}>
          <div className="bg-[#18181b] border border-[#27272a] rounded-lg p-6 max-w-md w-full mx-4" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold text-[#fafafa]">Detalles de Venta</h3>
              <Button onClick={() => setSelectedVenta(null)} size="sm" variant="ghost">✕</Button>
            </div>
            <div className="space-y-3">
              <div>
                <p className="text-[#a1a1aa] text-sm">Vendedor</p>
                <p className="text-[#fafafa] font-medium">{selectedVenta.vendedor_name}</p>
              </div>
              <div>
                <p className="text-[#a1a1aa] text-sm">Fecha</p>
                <p className="text-[#fafafa]">{formatDate(selectedVenta.created_at)}</p>
              </div>
              <div className="border-t border-[#27272a] pt-3">
                <p className="text-[#a1a1aa] text-sm mb-2">Productos:</p>
                {selectedVenta.items && selectedVenta.items.length > 0 ? (
                  selectedVenta.items.map((item, idx) => (
                    <div key={idx} className="flex justify-between items-center p-2 bg-[#09090b] rounded mb-2">
                      <div>
                        <p className="text-[#fafafa] font-medium">{item.product_name}</p>
                        <p className="text-[#a1a1aa] text-sm">{item.nicho_name}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-[#fafafa] font-bold">{formatCurrency(item.amount)}</p>
                        <p className="text-[#10b981] text-sm">+{formatCurrency(item.commission_earned)}</p>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="p-2 bg-[#09090b] rounded">
                    <p className="text-[#fafafa]">{selectedVenta.product_name || 'Producto único'}</p>
                    <p className="text-[#a1a1aa] text-sm">{selectedVenta.nicho_name}</p>
                    <p className="text-[#fafafa] font-bold mt-1">{formatCurrency(selectedVenta.amount)}</p>
                  </div>
                )}
              </div>
              <div className="border-t border-[#27272a] pt-3">
                <div className="flex justify-between text-lg">
                  <span className="text-[#fafafa] font-bold">Total:</span>
                  <span className="text-[#fafafa] font-bold">{formatCurrency(selectedVenta.total_amount || selectedVenta.amount)}</span>
                </div>
                <div className="flex justify-between text-sm mt-1">
                  <span className="text-[#10b981]">Comisión:</span>
                  <span className="text-[#10b981] font-bold">{formatCurrency(selectedVenta.total_commission || selectedVenta.commission_earned)}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;
